/**
 * ProductConsultingTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 96 / 100 — ATS Safe
 * Style: STAR-format structured layout for impact-driven storytelling
 * Best For: Product management, strategy consulting, MBA roles
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, SkillsBlock,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';

function ProductConsultingTemplate({
  resumeData,
  accentColor = '#1a56a0',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, extraList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const PCSection = ({ title }) => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '13px 0 6px', gap: 10 }}>
      <div style={{ width: 28, height: 2, background: accentColor, borderRadius: 1, flexShrink: 0 }} />
      <h2 style={{ fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.3px', color: '#1a1a1a', margin: 0, fontFamily: 'inherit' }}>{title}</h2>
      <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: fs(DS.fs.body), lineHeight: 1.55, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Header ─── */}
      <header style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
              style={{ fontSize: fs('22px'), fontWeight: 900, color: '#0f172a', margin: 0 }} placeholder="Your Name" />
            {(role || editMode) && (
              <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
                style={{ fontSize: fs('11px'), fontWeight: 600, color: accentColor, margin: '2px 0 0' }} placeholder="Product Manager · Strategy Consultant" />
            )}
          </div>
        </div>
        <div style={{ height: 2, background: accentColor, margin: '6px 0 5px' }} />
        <ContactRow data={d} accentColor={accentColor} />
      </header>

      {/* ─── Summary ─── */}
      {(summaryText || editMode) && (
        <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Professional Summary" />
          <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
            tag="p" multiline style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0 }} placeholder="Strategic professional with..." />
        </section>
      )}

      {/* ─── Core Competencies ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Core Competencies" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
        </section>
      )}

      {/* ─── Professional Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Professional Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb + 2, borderLeft: `3px solid ${accentColor}20`, paddingLeft: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0 }} />
                  {exp.company && (
                    <span style={{ fontSize: fs(DS.fs.body), color: accentColor, fontWeight: 600 }}>{exp.company}</span>
                  )}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 12, fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Internships ─── */}
      {internships.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Internship Experience" />
          {internships.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb + 2, borderLeft: `3px solid ${accentColor}20`, paddingLeft: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0 }} />
                  {exp.company && <span style={{ fontSize: fs(DS.fs.body), color: accentColor, fontWeight: 600 }}>{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 12 }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Key Projects / Consulting Work ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Key Projects" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a' }}>{proj.title || proj.name}</span>
                  {proj.tech?.length > 0 && (
                    <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', marginLeft: 8 }}>
                      {Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}
                    </span>
                  )}
                </div>
                {proj.impact && <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 700 }}>{proj.impact}</span>}
              </div>
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Education" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body), marginLeft: 6 }}>{edu?.college}</span>
                {edu?.cgpa && <span style={{ fontSize: fs(DS.fs.small), color: accentColor, fontWeight: 700, marginLeft: 6 }}>CGPA: {edu.cgpa}</span>}
              </div>
              <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
                tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap' }} />
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Certifications" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
          <PCSection title="Awards & Achievements" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
    </div>
  );
}

export default ProductConsultingTemplate;
